// Scroll-spy for a plan's side menu (today's markdown/useActiveHeading.ts):
// which section the reader is in. That is the
// last heading at or above a line near the top of the scrolling box. At the
// end of the scroll range, where the last sections can never reach that
// line, the heading the URL names wins while it is on screen, so a click on
// one of them in the outline marks it.

import { useEffect, useState } from 'react'
import { USER_CONTENT_PREFIX } from './sanitize'
import { useTabScope } from '../workspace/context'

function headingElement(id: string, root: ParentNode): HTMLElement | null {
  const elements = [...root.querySelectorAll<HTMLElement>('[id]')]
  return elements.find((element) => element.id === USER_CONTENT_PREFIX + id) ?? elements.find((element) => element.id === id) ?? null
}

/** How far below the top of the scrolling box a heading counts as reached. */
export const ACTIVE_LINE_PX = 96

export interface HeadingPositions {
  /** Each heading's top relative to the scrolling box; null when it is not shown. */
  tops: readonly (number | null)[]
  /** The scrolling box's visible height. */
  height: number
  /** The box is scrolled to its end (or does not scroll). */
  atEnd: boolean
  /** The heading id the URL's hash names, if any. */
  hashId: string | null
}

/** The active id among `ids` for the given positions (pure, for tests). */
export function pickActiveHeading(ids: readonly string[], at: HeadingPositions): string | null {
  const line = Math.min(ACTIVE_LINE_PX, at.height / 3)
  let active: string | null = null
  for (let i = 0; i < ids.length; i++) {
    const top = at.tops[i]
    if (top === null || top === undefined) continue
    if (top > line) break
    active = ids[i]!
  }
  if (at.atEnd && at.hashId) {
    const index = ids.indexOf(at.hashId)
    const top = index >= 0 ? at.tops[index] : null
    if (top !== null && top !== undefined && top >= -1 && top < at.height) return at.hashId
  }
  return active
}

function scrollingBox(element: Element): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY
    if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') return node
  }
  return null
}

function hashId(hash = window.location.hash): string | null {
  const raw = hash.replace(/^#/, '')
  if (!raw) return null
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function measure(ids: readonly string[], root: ParentNode, hash?: string): HeadingPositions | null {
  const elements = ids.map(id => headingElement(id, root))
  const first = elements.find((element) => element !== null)
  if (!first) return null
  const box = scrollingBox(first)
  const boxTop = box ? box.getBoundingClientRect().top : 0
  const height = box ? box.clientHeight : window.innerHeight
  const atEnd = box
    ? box.scrollTop + box.clientHeight >= box.scrollHeight - 2
    : window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2
  const tops = elements.map((element) => {
    if (!element) return null
    const rect = element.getBoundingClientRect()
    // Inside a closed <details>: not on screen at all.
    if (rect.width === 0 && rect.height === 0) return null
    return rect.top - boxTop
  })
  return { tops, height, atEnd, hashId: hashId(hash) }
}

/**
 * The id of the heading the reader is in, among `ids` (outline order), kept
 * current while any box on the page scrolls; null above the first one.
 */
export function useActiveHeading(ids: readonly string[]): string | null {
  const scope = useTabScope()
  const hash = scope ? new URL(scope.path(), 'http://workspace.invalid').hash : undefined
  const [active, setActive] = useState<string | null>(null)
  const key = ids.join('\u0000')

  useEffect(() => {
    const list = key ? key.split('\u0000') : []
    if (list.length === 0) {
      setActive(null)
      return
    }
    let frame: number | null = null
    const update = () => {
      frame = null
      const positions = measure(list, scope?.surface() ?? document, hash)
      const next = positions ? pickActiveHeading(list, positions) : null
      setActive((current) => (current === next ? current : next))
    }
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update)
    }
    update()
    // Scroll events do not bubble; capturing them on the document hears
    // the window and every scrolling box alike.
    document.addEventListener('scroll', schedule, { capture: true, passive: true })
    window.addEventListener('resize', schedule)
    window.addEventListener('hashchange', schedule)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      document.removeEventListener('scroll', schedule, { capture: true })
      window.removeEventListener('resize', schedule)
      window.removeEventListener('hashchange', schedule)
    }
  }, [key, scope, hash])

  return active
}
